/**
 * planner — crear en Microsoft Planner las tareas que salen del acta.
 *
 * El acta ya trae la tabla de compromisos con responsable y fecha. Lo que falta
 * es llevarlos a donde la gente los mira, que no es un Word guardado en
 * OneDrive.
 *
 * Dos decisiones que no son obvias:
 *
 * 1. **El responsable se busca entre los miembros del grupo del plan, no en el
 *    directorio.** Lo natural sería `/users?$filter=startswith(displayName,…)`,
 *    que es lo que hacía la pantalla vieja, pero eso exige `User.Read.All` y
 *    esta aplicación solo pide `User.Read`: en un tenant de verdad responde 403
 *    y la tarea se creaba sin asignar, en silencio. Con `Group.Read.All` —que sí
 *    se pide— se leen los miembros del grupo dueño del plan, que además es
 *    exactamente el conjunto de gente a la que Planner deja asignar. Sale mejor
 *    resuelto y con menos permisos.
 *
 * 2. **Ante la duda no se asigna a nadie.** En el acta los nombres vienen
 *    abreviados ("Iván R.", "Reyes") y en una empresa con dos Iván Rodríguez la
 *    tarea le caería al que no es sin que nadie se entere. Cuando hay varios
 *    candidatos se devuelven todos para que lo decida una persona.
 */

const GRAFO = 'https://graph.microsoft.com/v1.0';

export class PlannerError extends Error {
  constructor(mensaje, { code = null, status = null } = {}) {
    super(mensaje);
    this.name = 'PlannerError';
    this.code = code;
    this.status = status;
  }
}

async function grafo(ruta, token, opciones = {}) {
  // Los enlaces de paginacion que devuelve Graph son URLs absolutas.
  const url = /^https?:\/\//i.test(ruta) ? ruta : GRAFO + ruta;

  const respuesta = await fetch(url, {
    ...opciones,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(opciones.headers || {})
    }
  });

  if (respuesta.status === 204) return null;

  const texto = await respuesta.text();
  const datos = texto ? JSON.parse(texto) : null;

  if (!respuesta.ok) {
    const detalle = (datos && datos.error && datos.error.message) || respuesta.statusText;
    throw new PlannerError(detalle, {
      code: datos && datos.error ? datos.error.code : null,
      status: respuesta.status
    });
  }

  return datos;
}

/**
 * Todas las paginas de una coleccion de Graph.
 *
 * Graph nunca devuelve una lista larga entera: manda un trozo y un
 * `@odata.nextLink` con el resto. Pedir `$top=200` no cambia eso —es un maximo
 * por respuesta, no una promesa— asi que quien no siga el enlace se queda con
 * el primer trozo y no hay forma de notarlo desde fuera: la respuesta es
 * correcta, solo que incompleta.
 *
 * Es exactamente lo que pasaba con los miembros del equipo: en un grupo grande
 * faltaba gente en el desplegable de responsables, sin aviso ninguno.
 *
 * El tope existe para que un grupo de la empresa entera no encadene cincuenta
 * peticiones; se pone alto porque quedarse corto es el fallo que se viene a
 * arreglar.
 */
async function todasLasPaginas(ruta, token, limite = 2000) {
  const todo = [];
  let siguiente = ruta;

  while (siguiente && todo.length < limite) {
    const datos = await grafo(siguiente, token);
    todo.push(...((datos && datos.value) || []));
    siguiente = (datos && datos['@odata.nextLink']) || null;
  }

  return todo;
}

/**
 * Los planes donde se puede crear algo.
 *
 * ── Por qué se pregunta por dos caminos ──────────────────────────────
 *
 * Esto recorría solo los grupos de Microsoft 365 de la persona, pidiendo los
 * planes de cada uno. Parece razonable —«un plan cuelga siempre de un grupo»—
 * pero hace años que dejó de ser cierto: los planes que crea la aplicación
 * nueva de Planner, los planes compartidos de Teams y los que nacen en To Do
 * cuelgan de un *roster*, no de un grupo. Existen, la persona los ve en
 * Planner, y esta ventana no los ofrecía. Y no había forma de notarlo desde
 * dentro: la lista salía correcta, solo que sin ese plan.
 *
 * `/me/planner/plans` devuelve los dos tipos, que es justo lo que Planner le
 * enseña a uno. Se siguen recorriendo los grupos además, porque ese camino
 * alcanza planes de equipos a los que se pertenece pero que no están en el hub
 * personal. Se juntan los dos y se quitan los repetidos por id.
 *
 * No hace falta ningún permiso nuevo: `Tasks.ReadWrite`, que ya se pide para
 * poder crear las tareas, incluye leer los planes propios.
 */
export async function planesDisponibles(token) {
  const [delHub, deGrupos] = await Promise.all([
    planesDelHub(token),
    planesDeMisGrupos(token)
  ]);

  // Al juntar, un mismo plan puede venir por los dos lados. Se conserva el
  // nombre del equipo venga del lado que venga: es lo único que distingue a los
  // muchísimos planes llamados "Tareas".
  const porId = new Map();
  for (const plan of [...delHub, ...deGrupos]) {
    const previo = porId.get(plan.id);
    if (!previo) { porId.set(plan.id, plan); continue; }
    porId.set(plan.id, {
      ...previo,
      grupo: previo.grupo || plan.grupo,
      grupoId: previo.grupoId || plan.grupoId
    });
  }

  return [...porId.values()].sort((a, b) =>
    (a.grupo || '').localeCompare(b.grupo || '', 'es')
    || (a.titulo || '').localeCompare(b.titulo || '', 'es'));
}

/**
 * Los planes tal y como se los enseña Planner a la persona.
 *
 * Incluye los que no cuelgan de ningún grupo. Para esos, `grupoId` se queda en
 * nulo a propósito y quien lo use tiene que contar con ello: no hay equipo del
 * que sacar la lista de a quién se puede asignar.
 */
async function planesDelHub(token) {
  let planes;
  try {
    // Sin `$select`: la API de Planner no lo admite en todos sus recursos y
    // devolver el plan entero aquí no cuesta nada. Perder la lista por afinar
    // una proyección sería un mal negocio.
    planes = await todasLasPaginas('/me/planner/plans', token);
  } catch {
    // Si el hub no contesta quedan los grupos. Una lista corta estorba menos
    // que ninguna lista.
    return [];
  }

  const nombresDeGrupo = new Map();

  const salida = planes.map((p) => {
    const contenedor = p.container || null;
    // `owner` es como se decía antes de que existieran los rosters; los planes
    // viejos lo siguen trayendo.
    const esDeGrupo = contenedor ? contenedor.type === 'group' : Boolean(p.owner);
    const grupoId = esDeGrupo
      ? ((contenedor && contenedor.containerId) || p.owner || null)
      : null;
    if (grupoId) nombresDeGrupo.set(grupoId, null);
    return { id: p.id, titulo: p.title || '(plan sin título)', grupoId, grupo: null };
  });

  // El nombre del equipo se pide aparte porque el plan solo trae su id. Un
  // grupo que no se deje leer se queda sin nombre: molesta menos que esconder
  // el plan.
  await Promise.all([...nombresDeGrupo.keys()].map(async (id) => {
    try {
      const g = await grafo(`/groups/${id}?$select=id,displayName`, token);
      nombresDeGrupo.set(id, (g && g.displayName) || null);
    } catch { /* se queda sin nombre */ }
  }));

  for (const plan of salida) {
    if (plan.grupoId) plan.grupo = nombresDeGrupo.get(plan.grupoId) || null;
  }
  return salida;
}

/**
 * Los planes de los equipos a los que se pertenece.
 *
 * Se mira la pertenencia TRANSITIVA, no la directa. Quien llega a un equipo a
 * través de otro grupo —que es como se organizan casi todas las empresas con
 * más de un departamento— no aparecía en `/me/memberOf`, y con él desaparecían
 * los planes de ese equipo. Es el mismo fallo que ya se corrigió al leer los
 * miembros de un grupo, visto desde el otro lado.
 *
 * El `groupTypes` se filtra aquí y no en la consulta: filtrar por él en
 * `transitiveMemberOf` exige las consultas avanzadas de Graph (cabecera
 * `ConsistencyLevel`), y si eso falla se pierde la lista entera. Descartar dos
 * grupos de más en memoria sale gratis.
 */
async function planesDeMisGrupos(token) {
  let grupos;
  try {
    grupos = await todasLasPaginas(
      '/me/transitiveMemberOf/microsoft.graph.group?$select=id,displayName,groupTypes&$top=999',
      token
    );
  } catch {
    return [];
  }

  const unificados = grupos.filter(g =>
    g && g.id && Array.isArray(g.groupTypes) && g.groupTypes.includes('Unified'));

  const porGrupo = await Promise.all(
    unificados.map(async (grupo) => {
      try {
        // Con paginación: un equipo con muchos planes se quedaba en los
        // primeros, y la respuesta parecía completa.
        const planes = await todasLasPaginas(`/groups/${grupo.id}/planner/plans`, token);
        return planes.map(p => ({
          id: p.id,
          titulo: p.title || '(plan sin título)',
          grupoId: grupo.id,
          grupo: grupo.displayName || null
        }));
      } catch {
        // Un equipo que no usa Planner, o al que no se tiene acceso, no es un
        // error que haya que contarle a nadie.
        return [];
      }
    })
  );

  return porGrupo.flat();
}

/** Las columnas de un plan. Planner exige una al crear, y si no se dice, la primera. */
export async function bucketsDePlan(token, planId) {
  const datos = await grafo(`/planner/plans/${planId}/buckets?$select=id,name,orderHint`, token);
  return (datos.value || []).map(b => ({ id: b.id, nombre: b.name }));
}

/**
 * Quién está en el grupo dueño del plan.
 *
 * Son las únicas personas a las que Planner acepta asignar una tarea. No es una
 * decisión de esta aplicación: asignarle una tarea a alguien de fuera del grupo
 * lo rechaza el propio Planner. Por eso la lista no es el directorio de la
 * empresa, y no lo sería ni pidiendo permiso para leerlo — serían cientos de
 * personas a las que no se puede asignar nada.
 *
 * ── Faltaba gente, y por dos motivos a la vez ─────────────────────────
 *
 * **Los subgrupos.** Se pedía `/members`, que son los miembros DIRECTOS. Quien
 * pertenece al equipo a través de otro grupo —que es como se organizan casi
 * todas las empresas con más de un departamento— no aparecía. Con
 * `/transitiveMembers` se recorren los grupos anidados hasta el fondo.
 *
 * **La segunda página.** Se pedía `$top=200` sin seguir el `@odata.nextLink`.
 * Un equipo de más de doscientas personas se quedaba en las primeras
 * doscientas, y nada en la pantalla lo delataba.
 *
 * Se quitan los repetidos porque ahora pueden venir: alguien que esté en dos
 * subgrupos del mismo equipo sale dos veces, y en un desplegable eso parece un
 * error. Y se ordena por nombre, que es como se busca a una persona.
 */
export async function miembrosDelGrupo(token, grupoId) {
  // Un plan que cuelga de un roster y no de un grupo no tiene equipo del que
  // sacar esto. Devolver la lista vacía es lo honesto: no se sabe quién puede
  // recibir una tarea, y quien llame a esto tiene que distinguir «nadie» de
  // «no consta». Antes esto llamaba a `/groups/null/...` y tiraba la ventana
  // entera al elegir uno de esos planes.
  if (!grupoId) return [];

  const usuarios = await todasLasPaginas(
    `/groups/${grupoId}/transitiveMembers/microsoft.graph.user` +
    '?$select=id,displayName,mail,userPrincipalName&$top=999',
    token
  );

  const vistos = new Set();
  const miembros = [];
  for (const u of usuarios) {
    if (!u || !u.id || vistos.has(u.id)) continue;
    vistos.add(u.id);
    miembros.push({
      id: u.id,
      nombre: u.displayName || '',
      correo: u.mail || u.userPrincipalName || ''
    });
  }

  return miembros.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

/**
 * Todas las personas del directorio.
 *
 * ── Por que esto existe, y por que no basta ──────────────────────────
 *
 * Planner solo acepta asignar una tarea a alguien del grupo dueno del plan.
 * Eso no es una decision de esta aplicacion: es del servicio, y no hay permiso
 * que lo cambie. Asi que esta lista tiene, por fuerza, gente a la que la
 * asignacion va a fallar.
 *
 * Se ofrece igualmente porque se pidio verla, y porque el fallo se puede
 * manejar bien: `crearTarea` reintenta sin asignar cuando Planner rechaza al
 * destinatario, de modo que la tarea existe y se dice a quien no se le pudo
 * poner. Lo que no se hace es fingir: quien no esta en el equipo sale marcado
 * ANTES de crear nada.
 *
 * Se descartan las cuentas sin buzon —salas, equipos, servicios—: llenan la
 * lista de nombres que no son personas y a los que nadie va a asignar nada.
 *
 * Viene tambien el puesto, que no hace falta para asignar tareas pero si para
 * la tabla de asistentes del acta: ahi se teclea a mano el cargo de cada quien,
 * y el directorio ya lo sabe. Cabe en el mismo `$select` y en la misma
 * peticion.
 */
export async function personasDelDirectorio(token, limite = 2000) {
  const usuarios = await todasLasPaginas(
    '/users?$select=id,displayName,mail,userPrincipalName,jobTitle,accountEnabled,userType&$top=999',
    token,
    limite
  );

  // ── Quien sale en la lista: los compañeros, y nadie más ────────────────
  //
  // `/users` devuelve el directorio ENTERO, y ahí dentro hay tres cosas que no
  // son compañeros y que ensuciaban el desplegable hasta hacerlo inútil:
  //
  //   · **Invitados.** Un cliente al que se compartió un documento una vez
  //     entra en el directorio como invitado y aparecía entre quienes pueden
  //     ser responsables de un acuerdo interno.
  //
  //   · **Cuentas deshabilitadas.** Quien se fue de la empresa sigue en el
  //     directorio con su cuenta apagada, a veces durante años. Salía igual, y
  //     asignarle un acuerdo a alguien que ya no está es un compromiso que
  //     nadie va a cumplir y que nadie va a reclamar.
  //
  //   · **Buzones sin persona detrás.** Salas, equipos y cuentas de servicio no
  //     tienen `mail` propio en el sentido de una persona; se cuelan por el
  //     `userPrincipalName`.
  //
  // Lo que queda es quien tiene cuenta activa, es miembro de la organización y
  // tiene buzón: un compañero.
  return usuarios
    .filter(u => u && u.id && u.mail)
    .filter(u => u.accountEnabled !== false)
    .filter(u => String(u.userType || 'Member').toLowerCase() !== 'guest')
    .map(u => ({
      id: u.id,
      nombre: u.displayName || u.mail,
      correo: u.mail,
      puesto: u.jobTitle || ''
    }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

// ── Emparejar el nombre del acta con una persona ──────────────

/** Sin acentos, sin mayúsculas y sin puntos: "Iván R." y "ivan r" son lo mismo. */
function llano(texto) {
  return String(texto || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim();
}

const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'san', 'da', 'do', 'van', 'von']);

function piezas(nombre) {
  return llano(nombre).split(' ').filter(p => p && !PARTICULAS.has(p));
}

/**
 * La persona a la que se refiere un nombre escrito en el acta.
 *
 * En un acta nadie escribe el nombre completo del directorio: pone "Iván R.",
 * "Reyes" o "Blanca". Hay que reconocerlo sin llegar a adivinar.
 *
 * Se puntúa cada miembro y se devuelven TODOS los que empatan en lo más alto.
 * Es la parte importante: con dos "Iván R." la respuesta correcta no es elegir
 * uno, es decir que hay dos.
 *
 * @returns {{estado:'unico'|'varios'|'ninguno', candidatos:Array}}
 */
/**
 * Los separadores con los que un acta nombra a dos responsables.
 *
 * «Ana y Luis», «Ana / Luis», «Ana, Luis», «Ana & Luis», «Ana e Ignacio».
 * La `e` está porque en español la «y» se vuelve «e» ante i-: «Ana e Ignacio».
 */
const ENTRE_NOMBRES = /\s*(?:,|\/|&|\+|\by\b|\be\b)\s*/i;

/**
 * Las personas a las que se refiere una casilla de responsable.
 *
 * ── Por qué hace falta ───────────────────────────────────────────────────
 *
 * «Lo ven Ana y Luis» es una frase normal en una reunión, y la casilla del acta
 * acaba diciendo «Ana y Luis». `emparejarPersona` recibe UNA cadena y la puntúa
 * como si fuera el nombre de una persona: con «Ana y Luis» no encaja con nadie,
 * así que la tarea salía SIN ASIGNAR.
 *
 * La pantalla siempre admitió varias personas por tarea —hay fichas y un «+
 * Añadir a otra persona»— y Planner también. Lo que no sabía nadie era LEER dos
 * nombres de una casilla del acta.
 *
 * ── Por qué no se parte sin más ──────────────────────────────────────────
 *
 * Porque partir es peligroso: hay apellidos con «y» («Ortega y Gasset») y
 * nombres con comas. Partir a ciegas convertiría a una persona en dos
 * desconocidos y dejaría la tarea sin asignar, que es justo lo que se intenta
 * arreglar.
 *
 * Así que primero se prueba la casilla ENTERA. Solo si no encaja con nadie se
 * intenta partirla, y la partición se acepta únicamente cuando DOS O MÁS trozos
 * encuentran persona. Si solo uno encaja, se descarta la partición: era un
 * nombre con una «y» dentro, no dos personas.
 *
 * @returns {{estado:'unico'|'varios'|'ninguno'|'reparto', candidatos:Array,
 *            partes?:Array}}
 *   `reparto` es el caso nuevo: la casilla nombra a varias personas distintas,
 *   y `candidatos` las trae a todas. No confundir con `varios`, que significa
 *   lo contrario —un nombre que encaja con varias personas y no se sabe cuál—.
 */
export function emparejarResponsables(texto, miembros) {
  const entero = emparejarPersona(texto, miembros);
  if (entero.estado !== 'ninguno') return entero;

  const trozos = String(texto || '').split(ENTRE_NOMBRES)
    .map(t => t.trim())
    .filter(t => t.length > 1);
  if (trozos.length < 2) return entero;

  const porTrozo = trozos.map(t => ({ nombre: t, ...emparejarPersona(t, miembros) }));
  const resueltos = porTrozo.filter(p => p.estado === 'unico');

  // Una sola coincidencia no basta para creerse que eran dos nombres.
  if (resueltos.length < 2) return entero;

  // Sin repetir: «Ana y Ana Muñoz» apuntan a la misma persona.
  const vistos = new Set();
  const candidatos = [];
  for (const r of resueltos) {
    const id = r.candidatos[0] && r.candidatos[0].id;
    if (!id || vistos.has(id)) continue;
    vistos.add(id);
    candidatos.push(r.candidatos[0]);
  }
  if (candidatos.length < 2) return entero;

  return { estado: 'reparto', candidatos, partes: porTrozo };
}

export function emparejarPersona(nombre, miembros) {
  const buscadas = piezas(nombre);
  if (buscadas.length === 0 || !Array.isArray(miembros) || miembros.length === 0) {
    return { estado: 'ninguno', candidatos: [] };
  }

  const puntuado = miembros.map(persona => {
    const suyas = piezas(persona.nombre);
    const correo = llano(persona.correo).split('@')[0];
    if (suyas.length === 0) return { persona, puntos: 0 };

    // El nombre entero, tal cual: lo mejor que puede pasar.
    if (suyas.join(' ') === buscadas.join(' ')) return { persona, puntos: 100 };

    // Cada pieza buscada tiene que encontrar sitio en el nombre de la persona.
    // Una inicial suelta ("R.") vale por la pieza que empiece por esa letra;
    // exigirle más la dejaría sin emparejar nunca.
    const usadas = new Set();
    let encajadas = 0, exactas = 0;

    for (const pieza of buscadas) {
      let encontrada = -1;
      for (let i = 0; i < suyas.length; i++) {
        if (usadas.has(i)) continue;
        const suya = suyas[i];
        if (pieza.length === 1 ? suya.startsWith(pieza) : (suya === pieza || suya.startsWith(pieza))) {
          encontrada = i;
          if (suya === pieza) exactas++;
          break;
        }
      }
      if (encontrada >= 0) { usadas.add(encontrada); encajadas++; }
    }

    if (encajadas < buscadas.length) {
      // Puede seguir siendo la persona si lo que se escribió es su alias de
      // correo ("breyes"), que es como firma mucha gente.
      if (correo && buscadas.length === 1 && correo === buscadas[0]) return { persona, puntos: 70 };
      return { persona, puntos: 0 };
    }

    // Todas encajaron. Solo sube la nota por las que coincidieron enteras, no
    // por cuánto del nombre real se cubra: premiar la cobertura hacía que
    // "Iván" ganara con "Iván Rodríguez" —dos piezas— frente a "Iván Espadas
    // Espinosa" —tres—, y con tres Iván en el equipo eso es elegir por su
    // cuenta. Con esto los tres empatan y se pregunta, que es lo correcto.
    //
    // El caso que la cobertura pretendía resolver ya lo cubre la coincidencia
    // exacta de más arriba: "Blanca Reyes" saca 100 sobre "Blanca Reyes" y 60
    // sobre "Blanca Reyes Ortiz".
    return { persona, puntos: 40 + exactas * 10 };
  });

  const mejor = Math.max(...puntuado.map(p => p.puntos));
  if (mejor <= 0) return { estado: 'ninguno', candidatos: [] };

  const candidatos = puntuado.filter(p => p.puntos === mejor).map(p => p.persona);
  return { estado: candidatos.length === 1 ? 'unico' : 'varios', candidatos };
}

// ── Crear ─────────────────────────────────────────────────────

/**
 * Crea una tarea en el plan.
 *
 * `asignadoA` es el id de la persona —o varios ids— ya resueltos y
 * confirmados: aquí no se adivina nadie. Sin él la tarea se crea sin asignar,
 * que es un estado perfectamente válido en Planner y mejor que asignársela a
 * quien no es.
 *
 * ── Por qué admite varios ────────────────────────────────────────────────
 *
 * Porque un acuerdo se reparte. «Lo ven Ana y Luis» es una frase normal en una
 * reunión, y con un solo hueco había que elegir a uno —dejando al otro sin
 * tarea y sin aviso— o crear dos tareas iguales que luego nadie sabe si son la
 * misma. Planner lo admite de serie: `assignments` es un objeto con una entrada
 * por persona, no un campo.
 */
export async function crearTarea(token, { planId, bucketId = null, titulo, asignadoA = null, vence = null, nota = null }) {
  if (!planId) throw new PlannerError('Falta el plan donde crear la tarea');
  const nombre = String(titulo || '').trim();
  if (!nombre) throw new PlannerError('Una tarea sin texto no se puede crear');

  const cuerpo = {
    planId,
    // Planner corta por aquí; mandarlo más largo devuelve un error poco claro,
    // y el texto completo va igualmente en la descripción.
    title: nombre.slice(0, 255)
  };
  if (bucketId) cuerpo.bucketId = bucketId;
  if (vence) cuerpo.dueDateTime = vence;
  // Uno o varios: se normaliza a lista para no tener dos caminos.
  const aQuienes = (Array.isArray(asignadoA) ? asignadoA : [asignadoA]).filter(Boolean);
  if (aQuienes.length > 0) {
    cuerpo.assignments = Object.fromEntries(aQuienes.map(id => [
      id, { '@odata.type': '#microsoft.graph.plannerAssignment', orderHint: ' !' }
    ]));
  }

  // ── Cuando el destinatario no esta en el equipo del plan ──────────────
  //
  // Planner devuelve un error y NO crea nada. Perder la tarea por eso seria lo
  // peor: quien la mandaba queria sobre todo que existiera, y el responsable se
  // puede poner despues. Asi que se reintenta sin asignar y se devuelve dicho,
  // para que la pantalla lo cuente en vez de dar por buena una tarea que nadie
  // tiene.
  //
  // Se reintenta solo cuando habia asignacion: si falla sin ella, el problema
  // es otro y repetir la llamada solo crearia ruido.
  let tarea;
  let rechazoDelDestinatario = null;
  try {
    tarea = await grafo('/planner/tasks', token, {
      method: 'POST',
      body: JSON.stringify(cuerpo)
    });
  } catch (e) {
    if (aQuienes.length === 0) throw e;
    rechazoDelDestinatario = e.message || 'Planner no aceptó a esa persona.';
    delete cuerpo.assignments;
    tarea = await grafo('/planner/tasks', token, {
      method: 'POST',
      body: JSON.stringify(cuerpo)
    });
  }

  // La descripción va en otra llamada porque Planner la guarda en otro recurso.
  // Si falla, la tarea YA existe: se avisa, pero no se da por fallida —
  // reintentar crearía una segunda tarea igual.
  let notaPuesta = true;
  const descripcion = [nota, nombre.length > 255 ? nombre : null].filter(Boolean).join('\n\n');
  if (descripcion) {
    try {
      const detalles = await grafo(`/planner/tasks/${tarea.id}/details`, token);
      await grafo(`/planner/tasks/${tarea.id}/details`, token, {
        method: 'PATCH',
        headers: { 'If-Match': detalles['@odata.etag'] },
        body: JSON.stringify({ description: descripcion })
      });
    } catch {
      notaPuesta = false;
    }
  }

  return {
    id: tarea.id,
    titulo: tarea.title,
    // El enlace para abrirla en Planner. Se arma a mano porque Graph no lo
    // devuelve, y sin él la confirmación no lleva a ninguna parte.
    url: `https://tasks.office.com/Home/Task/${tarea.id}`,
    asignada: aQuienes.length > 0 && !rechazoDelDestinatario,
    // Por que se creo sin dueno, si se pidio uno. La pantalla lo necesita: una
    // tarea sin asignar que se creyo asignada es de las cosas que no se
    // descubren hasta que vence.
    rechazoDelDestinatario,
    vence: vence || null,
    notaPuesta
  };
}

const planner = {
  emparejarResponsables,
  planesDisponibles,
  bucketsDePlan,
  miembrosDelGrupo,
  personasDelDirectorio,
  emparejarPersona,
  crearTarea,
  PlannerError
};
export default planner;
